/**
 * Anmeldeseite.
 *
 * Vor der Anmeldung ist ausschliesslich diese Seite sichtbar: keine
 * Navigation, keine Produktdaten, keine Plattformstruktur (Auftrag Abschnitt 3).
 *
 * Sie darf bewusst dunkler und ruhiger wirken als die Arbeitsoberflaeche --
 * der Eingang zu einem internen Unternehmenssystem, kein Marketingauftritt.
 */
import { useState } from "react";
import { api, ApiError } from "../lib/api.ts";
import { FullMark } from "./Brand.tsx";
import { ParticleNetwork } from "./ParticleNetwork.tsx";
import { LanguageSwitch } from "./LanguageSwitch.tsx";
import { useI18n, usePageTitle } from "../i18n/index.tsx";
import { errorText } from "../i18n/errors.ts";
import type { SessionUser } from "@homebase/shared";
import "./Login.css";

export function Login({ onSignedIn, notice }: {
  onSignedIn: (u: SessionUser) => void;
  /** Hinweis aus dem vorherigen Zustand, etwa nach einer Passwortaenderung. */
  notice?: React.ReactNode;
}): React.JSX.Element {
  const i18n = useI18n();
  const t = i18n.t;
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  // Der Fehler wird als Code gehalten, nicht als fertiger Text. So folgt er
  // einer Sprachumschaltung, statt in der zuvor gewaehlten Sprache stehen zu
  // bleiben.
  const [error, setError] = useState<unknown>(null);

  usePageTitle(t("login.title"));

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api.login(username, password);
      if (r.user) onSignedIn(r.user);
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(0, "network", "network"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login">
      <ParticleNetwork />

      {/* Der Sprachschalter steht ausserhalb des Anmeldeformulars, ruhig in
          der oberen rechten Ecke. Das Formular selbst bleibt unveraendert. */}
      <div className="login__lang"><LanguageSwitch tone="dark" /></div>

      <main className="login__panel">
        <div className="login__brand"><FullMark /></div>

        <form className="login__form" onSubmit={submit}>
          <label className="field">
            <span className="field__label">{t("login.username")}</span>
            <input className="field__input" value={username} autoComplete="username" autoFocus
                   spellCheck={false} onChange={(e) => setUsername(e.target.value)} />
          </label>

          <label className="field">
            <span className="field__label">{t("login.password")}</span>
            <input className="field__input" type="password" value={password}
                   autoComplete="current-password" onChange={(e) => setPassword(e.target.value)} />
          </label>

          <div className="login__message" role="alert" aria-live="polite">
            {error !== null && <span className="login__error">{errorText(i18n, error)}</span>}
            {error === null && notice && <span className="login__notice">{notice}</span>}
          </div>

          <button className="btn btn--primary login__submit" disabled={busy || !username || !password}>
            {busy ? t("login.submitBusy") : t("login.submit")}
          </button>
        </form>
      </main>

      <p className="login__foot">{t("login.foot")}</p>
    </div>
  );
}
